import {StateCreator} from 'zustand';
import {applyPatches, Patch, produce} from 'immer';

type PrefixedKeys<T extends Obj, TPrefix extends string> = {
  [K in keyof T as `${TPrefix}${string & K}`]: T[K];
};

// this state will be prefixed - hence the capitalization
type StateWithHistory<T extends Obj> = {
  State?: T;
  History: Array<[Patch[], Patch[]]>;
  HistoryIndex: number;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  SetState: (state: T, updateHistory?: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Undo: () => T | undefined;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Redo: () => T | undefined;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  ClearHistory: () => void;
};

export function createStateWithHistorySlice<T extends Obj, TPrefix extends string>(
  prefix: TPrefix,
): StateCreator<PrefixedKeys<StateWithHistory<T>, TPrefix>> {
  return (set, get) =>
    ({
      [`${prefix}History`]: [],
      [`${prefix}HistoryIndex`]: 0,
      [`${prefix}SetState`]: (state: T, updateHistory: boolean = true) => {
        const prevState = get()[`${prefix}State`] as StateWithHistory<T>[`State`];
        const history = get()[`${prefix}History`] as StateWithHistory<T>[`History`];
        let historyIndex = get()[`${prefix}HistoryIndex`] as StateWithHistory<T>[`HistoryIndex`];

        const nextState = produce(
          prevState,
          () => state,
          (patches, inversePatches) => {
            if (!updateHistory) {
              return;
            }
            if (historyIndex < history.length) {
              history.splice(historyIndex);
            }
            history.push([patches, inversePatches]);
            historyIndex = history.length;
          },
        );

        set({
          [`${prefix}State`]: nextState,
          [`${prefix}History`]: history,
          [`${prefix}HistoryIndex`]: historyIndex,
        } as Partial<PrefixedKeys<StateWithHistory<T>, TPrefix>>);
      },
      [`${prefix}Undo`]: () => {
        const state = get()[`${prefix}State`] as StateWithHistory<T>[`State`];
        const history = get()[`${prefix}History`] as StateWithHistory<T>[`History`];
        const historyIndex = get()[`${prefix}HistoryIndex`] as StateWithHistory<T>[`HistoryIndex`];
        if (historyIndex <= 0) {
          return undefined;
        }
        const [_, inversePatches] = history[historyIndex - 1];
        const nextState = applyPatches(state ?? {}, inversePatches);
        set({
          [`${prefix}State`]: nextState,
          [`${prefix}HistoryIndex`]: historyIndex - 1,
        } as Partial<PrefixedKeys<StateWithHistory<T>, TPrefix>>);
        return nextState;
      },
      [`${prefix}Redo`]: () => {
        const state = get()[`${prefix}State`] as StateWithHistory<T>[`State`];
        const history = get()[`${prefix}History`] as StateWithHistory<T>[`History`];
        const historyIndex = get()[`${prefix}HistoryIndex`] as StateWithHistory<T>[`HistoryIndex`];
        if (historyIndex > history.length - 1) {
          return undefined;
        }
        const [patches] = history[historyIndex];
        const nextState = applyPatches(state ?? {}, patches);
        set({
          [`${prefix}State`]: nextState,
          [`${prefix}HistoryIndex`]: historyIndex + 1,
        } as Partial<PrefixedKeys<StateWithHistory<T>, TPrefix>>);
        return nextState;
      },
      [`${prefix}ClearHistory`]: () => {
        set({
          [`${prefix}History`]: [],
          [`${prefix}HistoryIndex`]: 0,
        } as Partial<PrefixedKeys<StateWithHistory<T>, TPrefix>>);
      },
    }) as PrefixedKeys<StateWithHistory<T>, TPrefix>;
}
